// Поиск витрины и цифровые UGC-артикулы (ТЗ 31.10.2026, п. 4.1).
//
// Запрос из 8 цифр (первая не 0) сначала проверяется по реестру артикулов:
//   - активный артикул - переход засчитывается (`POST /articles/visit`),
//     код запоминается на 30 дней, покупатель сразу попадает на карточку;
//   - не найден / отключён - обычная выдача каталога по этому запросу
//     (системный SKU варианта тоже может состоять из цифр) с сообщением
//     «Товар по артикулу не найден» и подборкой популярного, если выдача пуста.
// Шлюз дублирует резолв в ответе поиска (`ugcRedirect`) - это закрывает
// прямую ссылку /search?q=12345678, когда поле поиска не нажимали.
import { looksLikeUgcArticle, UgcResolveResultSchema } from "@osmix/contracts/ugc-articles";
import type { NavigateFunction } from "react-router";

import { api, type UgcResolveResult } from "@/api";
import { useWishlist } from "@/store";

import { rememberDiscoverySource } from "./discovery-source";
import { extendUgcAttribution, saveUgcAttribution } from "./ugc-attribution";

export { looksLikeUgcArticle };

export type UgcFound = Extract<UgcResolveResult, { found: true }>;

/** Состояние навигации карточки товара, открытой по артикулу. */
export type UgcNavState = { ugcCode?: string };

export const ugcResolveKey = (code: string) => ["ugc", "resolve", code.trim()] as const;

/** Резолв кода. Ошибка сети - null: тогда поиск идёт обычным путём. */
export const resolveUgcCode = async (code: string): Promise<UgcResolveResult | null> => {
	try {
		return await api.ugc.resolve(code.trim());
	} catch {
		return null;
	}
};

/** `ugcRedirect` из ответа поиска - разобрать схемой контракта. */
export const ugcRedirectOf = (raw: unknown): UgcFound | null => {
	if (raw == null) return null;
	const parsed = UgcResolveResultSchema.safeParse(raw);
	if (!parsed.success) return null;
	const value = parsed.data as UgcResolveResult;
	return value.found ? value : null;
};

/**
 * Открыть карточку по найденному артикулу: запомнить код, засчитать переход
 * (без ожидания - карточка не ждёт статистику) и перейти на `productPath`.
 */
export const enterUgcArticle = (found: UgcFound, navigate: NavigateFunction, opts: { replace?: boolean } = {}): void => {
	saveUgcAttribution({
		code: found.code,
		articleId: found.articleId,
		productId: found.productId,
		shopId: found.shopId,
		partnerId: found.partnerId,
	});
	// Путь к товару - «Поиск»: покупатель пришёл со своим запросом.
	rememberDiscoverySource("search");
	let clientId: string | undefined;
	try {
		clientId = useWishlist.getState().clientId;
	} catch {
		clientId = undefined;
	}
	if (clientId) {
		api.ugc
			.visit({ code: found.code, clientId, surface: "web" })
			.then((visit) => extendUgcAttribution(found.code, visit.expiresAt))
			.catch(() => undefined);
	}
	const path = found.productPath?.startsWith("/") ? found.productPath : `/product/${encodeURIComponent(found.productId)}`;
	navigate(path, { replace: opts.replace, state: { ugcCode: found.code } satisfies UgcNavState });
};
